import type { BillingAdjustmentType, PaginationType } from "@/types"
import { ColumnDef } from "@tanstack/react-table"
import { format } from "date-fns"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"

import { DataTableColumnHeader } from "../../data-table"
import { BillingAdjustmentActions } from "../operations/billing-adjustment-operation"

export const billingAdjustmentColumns: ColumnDef<BillingAdjustmentType>[] = [
  {
    accessorKey: "actions",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Actions" />
    ),
    cell: ({ row }) => {
      if (row.original.adjustment_status !== "PENDING") return null

      return <BillingAdjustmentActions adjustmentData={row.original} />
    },
  },
  {
    accessorKey: "adjustment_id",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Adjustment ID" />
    ),
    cell: ({ row }) => {
      return <div className="print:text-xl">{row.original.adjustment_id}</div>
    },
  },
  {
    accessorKey: "billing_id",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Billing ID" />
    ),
    cell: ({ row }) => {
      return <div className="print:text-xl">{row.original.billing_id}</div>
    },
  },

  {
    accessorKey: "consumer_name",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Consumer" />
    ),
    cell: ({ row }) => {
      return <div className="print:text-xl">{row.original.consumer_name}</div>
    },
  },
  {
    accessorKey: "total_previous_consumption",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Prev. Consumption" />
    ),
    cell: ({ row }) => {
      return (
        <div className="print:text-xl">
          {row.original.previous_consumption ||
            row.original.previous_consumption}
        </div>
      )
    },
  },
  {
    accessorKey: "total_previous_due_amount",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Prev. Due Amount" />
    ),
    cell: ({ row }) => {
      return (
        <div className="print:text-xl">
          {row.original.previous_due_amount || row.original.previous_due_amount}
        </div>
      )
    },
  },
  {
    accessorKey: "total_new_consumption",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="New Consumption" />
    ),
    cell: ({ row }) => {
      return (
        <div className="print:text-xl">
          {row.original.new_consumption || row.original.new_consumption}
        </div>
      )
    },
  },
  {
    accessorKey: "total_new_due_amount",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="New Due Amount" />
    ),
    cell: ({ row }) => {
      return (
        <div className="print:text-xl">
          {(row.original?.new_due_amount.toFixed(2) ?? 0.00) || (row.original?.new_due_amount.toFixed(2) ?? 0.00)}
        </div>
      )
    },
  },
  {
    accessorKey: "adjustment_reason",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Adjustment Reason" />
    ),
    cell: ({ row }) => {
      return (
        <div className="print:text-xl">{row.original.adjustment_reason}</div>
      )
    },
  },
  {
    accessorKey: "adjustment_notes",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Adjustment Notes" />
    ),
    cell: ({ row }) => {
      return (
        <div className="print:text-xl">{row.original.adjustment_notes}</div>
      )
    },
  },
  {
    accessorKey: "adjustment_status",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Adjustment Status" />
    ),
    cell: ({ row }) => {
      const status = row.original.adjustment_status
      const statusClasses: any = {
        PENDING: "bg-gray-200 text-gray-800",
        APPROVED: "bg-green-200 text-green-800",
        DENIED: "bg-red-200 text-red-800",
      }

      return (
        <Badge
          className={`px-2 py-1 print:text-xl ${statusClasses[status] || "bg-gray-100 text-gray-600"}`}
        >
          {status}
        </Badge>
      )
    },
  },
  {
    accessorKey: "adjustment_date",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Adjustment Date" />
    ),
    cell: ({ row }) => {
      const value = row.original.adjustment_date
      return (
        <div className="print:text-xl">
          {value ? format(new Date(value), "MMM dd, yyyy") : ""}
        </div>
      )
    },
  },
  {
    accessorKey: "requested_by",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Requested By" />
    ),
    cell: ({ row }) => {
      const value = row.original.adjustment_date
      return <div className="print:text-xl">{row.original.requested_by}</div>
    },
  },
  {
    accessorKey: "date_approved_denied",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Date Approved/Denied" />
    ),
    cell: ({ row }) => {
      const value = row.original.date_approved_denied
      return (
        <div className="print:text-xl">
          {value ? format(new Date(value), "MMM dd, yyyy") : ""}
        </div>
      )
    },
  },
  {
    accessorKey: "approved_denied_by",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Approved/Denied By" />
    ),
    cell: ({ row }) => {
      const value = row.original.approved_denied_by
      return (
        <div className="print:text-xl">{row.original.approved_denied_by}</div>
      )
    },
  },
]
